import { SendOutlined } from '@ant-design/icons';
import { Button, Input } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { BluePrimary, Gray4, Gray5 } from '../../../styles';

const InputWrapper = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  align-items: flex-start;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`;

const StyledInputWithPadding = styled(Input.TextArea)`
  padding: 14px 46px 14px 12px;
  border: 1px solid ${Gray5};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  resize: none !important;
  width: 100%;
  background-color: ${Gray4};

  &:hover,
  &:focus {
    border-color: ${BluePrimary};
    box-shadow: 0 2px 12px rgba(22, 119, 255, 0.15);
  }
`;

const InputBottomRight = styled.div`
  position: absolute;
  right: 8px;
  bottom: 8px;
  z-index: 1;
`;

const SendButton = styled(Button)`
  background-color: ${BluePrimary};
  border-color: ${BluePrimary};
  height: 36px;

  &:hover {
    background-color: #4096ff !important;
    border-color: #4096ff !important;
  }

  &:disabled {
    background-color: ${Gray5};
    border-color: ${Gray5};
  }
`;

function MessageInput({
  inputValue,
  setInputValue,
  sendMessage,
  hasMessages,
  loading,
  scrollToBottom = () => {},
}) {
  const inputRef = useRef(null);

  // Auto-focus input on mount when no messages
  useEffect(() => {
    if (!hasMessages && inputRef.current) {
      // Small delay to ensure the component is fully rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [hasMessages]);

  const handleSendMessage = () => {
    if (!inputValue.trim() || loading) return;

    sendMessage({ data: { message: inputValue, analysis_mode: 'ai-analyst' } });

    setInputValue('');
    scrollToBottom();
  };

  return (
    <InputWrapper>
      <StyledInputWithPadding
        ref={inputRef}
        variant="borderless"
        placeholder="Ask me to analyze your data..."
        data-cy="chat-input"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onPressEnter={(e) => {
          if (!e.shiftKey && inputValue.trim() && !loading) {
            e.preventDefault();
            handleSendMessage();
          }
        }}
        autoSize={{ minRows: 1, maxRows: 8 }}
      />
      <InputBottomRight>
        <SendButton
          onClick={handleSendMessage}
          type="primary"
          icon={<SendOutlined />}
          data-cy="chat-send-btn"
          disabled={loading || !inputValue.trim()}
        />
      </InputBottomRight>
    </InputWrapper>
  );
}

MessageInput.propTypes = {
  inputValue: PropTypes.string.isRequired,
  setInputValue: PropTypes.func.isRequired,
  sendMessage: PropTypes.func.isRequired,
  hasMessages: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  scrollToBottom: PropTypes.func,
};

export default MessageInput;
